/**
 * Input layer: controller buttons and keyboard → debounced/auto-repeated actions (SPEC §5.1).
 * Keyboard fallback is always on; controller buttons go through the user's mapping.
 */
import { hub } from '@/devices/hub';
import { ButtonInput } from '@/devices/click/buttons';
import { getSettings } from '@/store/settings';
import type { ControlAction } from '@/store/settingsModel';
import { Emitter } from '@/trainer/types';
import { dispatchAction, keyToAction } from './actions';

const REPEATABLE: ControlAction[] = ['shiftUp', 'shiftDown', 'ergUp', 'ergDown'];

/** Raw button presses (for the "press a button → assign" UI). */
export const rawButtons = new Emitter<string>();

function actionForButton(button: string): ControlAction {
  if (button.startsWith('key:')) {
    return keyToAction({ key: button.slice(4), code: button.slice(4) }) ?? 'none';
  }
  return getSettings().buttonMapping[button] ?? 'none';
}

const input = new ButtonInput(
  (button, repeat) => {
    const a = actionForButton(button);
    if (repeat && !REPEATABLE.includes(a)) return;
    if (a !== 'none') dispatchAction(a);
  },
  (button) => REPEATABLE.includes(actionForButton(button)),
);

let started = false;

/** Wire controller buttons into the input layer (once). */
export function startControllerInput(): void {
  if (started) return;
  started = true;
  hub.buttons.on((e) => {
    if (e.pressed) rawButtons.emit(e.button);
    if (!captureActive) input.set(e.button, e.pressed);
  });
}

let captureActive = false;
/** While assigning a button in the mapping UI, presses are not dispatched as actions. */
export function setCapture(active: boolean): void {
  captureActive = active;
}

/** Keyboard (ride screen): Arrow keys repeat with our own timing; OS key-repeat events are ignored. */
export function keyDown(e: KeyboardEvent): boolean {
  const a = keyToAction(e);
  if (!a) return false;
  if (!e.repeat) input.set(`key:${e.key === ' ' ? ' ' : e.key}`, true);
  return true;
}

export function keyUp(e: KeyboardEvent): void {
  if (!keyToAction(e)) return;
  input.set(`key:${e.key === ' ' ? ' ' : e.key}`, false);
}

export function releaseAllKeys(): void {
  input.releaseAll();
}
